import { Injectable } from '@angular/core';
import { FormArray, FormGroup, NgForm } from '@angular/forms';
import {
  FormErrorDto,
  IFormExceptionDto,
} from '@models/exceptions/form-error-dto';
import { ResponseDto } from '@models/exceptions/response-dto';
import {
  IRuleExceptionDto,
  RuleErrorDto,
} from '@models/exceptions/rule-error-dto';
import { IUserPendingValidationsDto } from '@models/portal/validator/dto/user-pending-validation-dto';
import {
  userAddressFieldReference,
  userBasicDataFieldReference,
} from '@modules/user/user-form/requitred-fields-config';
import {
  AttachmentViewFg,
  DocumentViewFg,
  UserStepValidationEnum,
} from '@modules/user/user-form/user-form.component';
import { SnackbarService } from '@shared/services/snackbar.service';

@Injectable({
  providedIn: 'root',
})
export class HandlerErrorMessage {
  protected validations: Map<string, string> = new Map([
    ['required', 'Este campo é obrigatório.'],
    ['minlength', 'O campo não atingiu o mínimo de caracteres.'],
    ['maxlength', 'O campo excedeu o máximo de caracteres.'],
    [
      'apiError',
      'Erro ao processar a solicitação. Por favor, tente novamente mais tarde.',
    ],
    [
      'valorNaLista',
      'Valor inválido. Selecione uma opção da lista disponível.',
    ],
    ['mask', 'O valor inserido não corresponde ao formato esperado.'],
    ['min', 'O valor deve ser maior que o mínimo permitido.'],
    ['minDigits', 'O valor deve ser maior que o mínimo permitido.'],
    ['max', 'O valor excedeu o máximo permitido.'],
    ['email', 'Digite um endereço de e-mail válido.'],
    [
      'pattern',
      'O formato do campo é inválido. Por favor, siga o padrão especificado.',
    ],
    [
      'birthdate',
      'A data de nascimento não pode ser posterior a data atual e anterior a 100 anos atrás.',
    ],
    [
      'usuDtNascimento',
      'A data de nascimento não pode ser posterior a data atual e anterior a 100 anos atrás.',
    ],
    ['passwordMatch', 'A senha não coincide.'],
    ['whitespace', 'O campo não pode conter apenas espaços em branco.'],
    ['userValidation', ''],
    ['whitespace', 'O campo não pode conter apenas espaços em branco.'],
    ['requiredTrue', 'Você deve aceitar este campo.'],
    ['finalDateBeforeStart', 'Não pode ser anterior à data de início.'],
    ['initialDateBeforeToday', 'Não pode ser anterior a hoje.'],
  ]);

  constructor(private readonly snackbarService: SnackbarService) {}

  resolveMessage(
    responseDto: ResponseDto<any>,
    handleMessageError: HandleMessageErrorStrategy = 'snackbar',
  ): void {
    if (responseDto.type === 0) {
      const ruleExceptionDto: RuleErrorDto =
        responseDto.response as IRuleExceptionDto;
      this.handleMessageErrorStrategyFn(ruleExceptionDto, handleMessageError);
    }
  }

  setErrorMessageInFormField(
    responseDto: ResponseDto<any>,
    form: NgForm | FormGroup,
    handleMessageError: HandleMessageErrorStrategy = 'snackbar',
  ): void {
    if (responseDto.type === 0) {
      const ruleExceptionDto: RuleErrorDto =
        responseDto.response as IRuleExceptionDto;
      this.handleMessageErrorStrategyFn(ruleExceptionDto, handleMessageError);
    } else if (responseDto.type === 1) {
      const formExceptionDto: FormErrorDto =
        responseDto.response as IFormExceptionDto;

      formExceptionDto.errors.forEach((fieldError) => {
        this.validations.set('apiError', fieldError.message);

        if (form instanceof NgForm) {
          form.form.controls[fieldError.label].setErrors({ apiError: true });
          form.form.controls[fieldError.label].markAsTouched();
        } else if (form instanceof FormGroup) {
          form.controls[fieldError.label].setErrors({ apiError: true });
          form.controls[fieldError.label].markAsTouched();
        }
      });
    }
  }

  handleMessageErrorStrategyFn(
    ruleExceptionDto: RuleErrorDto,
    handleMessageError: HandleMessageErrorStrategy,
  ): void {
    switch (handleMessageError) {
      case 'snackbar':
        this.snackbarService.showDanger(ruleExceptionDto.message);
        break;
    }
  }

  handleUserValidationFields(
    pendingValidations: IUserPendingValidationsDto,
    form: FormGroup | FormArray,
    userStepValidation: UserStepValidationEnum,
  ) {
    const isFormGroup = form instanceof FormGroup;
    const isFormArray = form instanceof FormArray;

    if (isFormGroup && userStepValidation === UserStepValidationEnum.USUARIO) {
      pendingValidations?.uvcListCampos?.forEach((fieldEnum) => {
        const fieldName = userBasicDataFieldReference[fieldEnum];

        this.validations.set('userValidation', 'Campo obrigatório.');

        form.controls[fieldName].setErrors({ userValidation: true });
        form.controls[fieldName].markAsTouched();
      });
    } else if (
      isFormGroup &&
      userStepValidation === UserStepValidationEnum.LOGRADOURO
    ) {
      pendingValidations?.uvcListLogradouro?.forEach((fieldEnum) => {
        const fieldName = userAddressFieldReference[fieldEnum];

        this.validations.set('userValidation', 'Campo obrigatório.');

        form.controls[fieldName].setErrors({ userValidation: true });
        form.controls[fieldName].markAsTouched();
      });
    } else if (
      isFormArray &&
      userStepValidation === UserStepValidationEnum.CONTATO
    ) {
      // TODO: Implementar lógica para campos de contato
    } else if (
      isFormArray &&
      userStepValidation === UserStepValidationEnum.DOCUMENTO
    ) {
      pendingValidations?.uvcListDocumentos?.forEach((fieldEnum) => {
        const fg = (form as FormArray<FormGroup<DocumentViewFg>>).controls.find(
          (fg) => fg.controls.docTxTipo.value === fieldEnum,
        );

        this.validations.set('userValidation', 'Campo obrigatório.');

        fg?.controls.docTxDocumento.setErrors({ userValidation: true });
        fg?.controls.docTxDocumento.markAsTouched();
        fg?.updateValueAndValidity();
      });

      form.updateValueAndValidity();
    } else if (
      isFormArray &&
      userStepValidation === UserStepValidationEnum.ANEXO
    ) {
      pendingValidations?.uvcListAnexos?.forEach((fieldEnum) => {
        const fg = (
          form as FormArray<FormGroup<AttachmentViewFg>>
        ).controls.find((fg) => fg.controls.aneTxTipoAnexo.value === fieldEnum);

        this.validations.set('userValidation', 'Campo obrigatório.');

        fg?.controls.aneBtAnexo.setErrors({ userValidation: true });
        fg?.controls.aneBtAnexo.markAsTouched();
        fg?.updateValueAndValidity();
      });

      form.updateValueAndValidity();
    }
  }

  showErrorMessageForKey(form: NgForm | FormGroup, key: string): string {
    let message: string | undefined;

    const formControl =
      form instanceof NgForm ? form.form.get(key) : form.get(key);

    if (formControl?.invalid && (formControl?.touched || formControl?.dirty)) {
      this.validations.forEach((_, validationKey) => {
        if (formControl.errors?.[validationKey]) {
          message = this.validations.get(validationKey);
        }
      });
    }
    return message ? message : '';
  }
}

export type HandleMessageErrorStrategy = 'popup' | 'snackbar' | 'alert';
